/**
 * NotificationBell — lightweight in-app inbox.
 *
 * Signed-in only. Polls /api/notifications every 60s, shows an unread badge,
 * and opens a dropdown panel with mark-as-read controls. No external
 * delivery — this bell is the only notification surface.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Bell, CheckCheck, Flag, Inbox, Settings2, ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { NOTIF, NOTIF_PREFS } from '@/constants/testIds';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

export default function NotificationBell({ mobile = false, onNavigate }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const [showPrefs, setShowPrefs] = useState(false);
    const [prefs, setPrefs] = useState(null); // {types, muted_types}
    const navigate = useNavigate();
    const rootRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get('/notifications');
            setItems(data.notifications || []);
            setUnread(data.unread_count || 0);
        } catch { /* signed out or transient — keep quiet */ }
    }, []);

    useEffect(() => {
        if (!user) return undefined;
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, [user, load]);

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    if (!user) return null;

    const openPrefs = async () => {
        setShowPrefs(true);
        try {
            const { data } = await api.get('/notifications/prefs');
            setPrefs(data);
        } catch {
            setPrefs({ types: [], muted_types: [] });
        }
    };

    const toggleMute = async (type) => {
        if (!prefs) return;
        const muted = prefs.muted_types.includes(type)
            ? prefs.muted_types.filter((t) => t !== type)
            : [...prefs.muted_types, type];
        setPrefs({ ...prefs, muted_types: muted });
        try {
            await api.post('/notifications/prefs', { muted_types: muted });
        } catch { /* keep optimistic state; next open re-syncs */ }
    };

    const markAll = async () => {
        try {
            await api.post('/notifications/read', { all: true });
            setUnread(0);
            setItems((prev) => prev.map((n) => ({ ...n, read: true })));
        } catch { /* ignore */ }
    };

    const openItem = async (n) => {
        if (!n.read) {
            try {
                await api.post('/notifications/read', { ids: [n.notification_id] });
                setUnread((u) => Math.max(0, u - 1));
                setItems((prev) => prev.map((x) =>
                    x.notification_id === n.notification_id ? { ...x, read: true } : x));
            } catch { /* ignore */ }
        }
        if (n.link) {
            setOpen(false);
            onNavigate?.();
            navigate(n.link);
        }
    };

    return (
        <div className="relative" ref={rootRef}>
            <button
                onClick={() => { setOpen((v) => !v); if (!open) load(); }}
                data-testid={mobile ? NOTIF.mobileBell : NOTIF.bell}
                aria-label="Notifications"
                className={cn(
                    'relative flex items-center justify-center transition-colors',
                    mobile
                        ? 'w-10 h-10 rounded-xl glass hover:bg-white/10'
                        : 'w-9 h-9 rounded-full glass border border-white/10 hover:border-white/30',
                )}
            >
                <Bell className="w-4 h-4 text-white/80" />
                {unread > 0 && (
                    <span
                        data-testid={NOTIF.badge}
                        className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-lab-pink text-black text-[9px] font-bold flex items-center justify-center"
                    >
                        {unread > 9 ? '9+' : unread}
                    </span>
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 6, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 4, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        data-testid={NOTIF.panel}
                        className={cn(
                            'absolute z-[60] w-[340px] max-w-[86vw] glass-strong rounded-2xl border border-white/10 overflow-hidden shadow-2xl',
                            mobile ? 'left-0 top-12' : 'right-0 top-11',
                        )}
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
                            {showPrefs ? (
                                <button
                                    onClick={() => setShowPrefs(false)}
                                    data-testid={NOTIF_PREFS.back}
                                    className="lab-mono text-[10px] text-lab-mute hover:text-white tracking-[0.18em] inline-flex items-center gap-1.5"
                                >
                                    <ArrowLeft className="w-3 h-3" /> PREFERENCES
                                </button>
                            ) : (
                                <div className="lab-mono text-[10px] text-lab-mute tracking-[0.18em]">
                                    NOTIFICATIONS
                                </div>
                            )}
                            <div className="flex items-center gap-3">
                                {!showPrefs && unread > 0 && (
                                    <button
                                        onClick={markAll}
                                        data-testid={NOTIF.markAll}
                                        className="lab-mono text-[9px] text-lab-blue hover:text-lab-emerald inline-flex items-center gap-1 tracking-[0.12em]"
                                    >
                                        <CheckCheck className="w-3 h-3" /> MARK ALL READ
                                    </button>
                                )}
                                {!showPrefs && (
                                    <button
                                        onClick={openPrefs}
                                        data-testid={NOTIF_PREFS.gear}
                                        aria-label="Notification preferences"
                                        className="text-lab-mute hover:text-white transition-colors"
                                    >
                                        <Settings2 className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>
                        {showPrefs ? (
                            <div data-testid={NOTIF_PREFS.panel} className="p-4">
                                <div className="text-lab-mute text-[11px] mb-3">
                                    Muted events are never delivered to your bell.
                                </div>
                                {!prefs && (
                                    <div className="text-lab-dim text-xs italic">Loading…</div>
                                )}
                                {prefs && prefs.types.map((t) => {
                                    const muted = prefs.muted_types.includes(t.type);
                                    return (
                                        <button
                                            key={t.type}
                                            onClick={() => toggleMute(t.type)}
                                            data-testid={NOTIF_PREFS.toggle(t.type)}
                                            className="w-full flex items-center justify-between gap-3 py-2.5 border-b border-white/5 text-left"
                                        >
                                            <span className={cn('text-xs', muted ? 'text-lab-mute' : 'text-white')}>
                                                {t.label}
                                            </span>
                                            <span
                                                className={cn(
                                                    'relative w-9 h-5 rounded-full transition-colors shrink-0',
                                                    muted ? 'bg-white/10' : 'bg-lab-blue/70',
                                                )}
                                            >
                                                <span
                                                    className={cn(
                                                        'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all',
                                                        muted ? 'left-0.5' : 'left-[18px]',
                                                    )}
                                                />
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                        <div className="max-h-[360px] overflow-y-auto">
                            {items.length === 0 && (
                                <div data-testid={NOTIF.empty} className="py-10 text-center">
                                    <Inbox className="w-6 h-6 text-lab-mute mx-auto mb-2" />
                                    <div className="text-lab-mute text-xs">Nothing yet.</div>
                                </div>
                            )}
                            {items.map((n) => (
                                <button
                                    key={n.notification_id}
                                    onClick={() => openItem(n)}
                                    data-testid={NOTIF.item(n.notification_id)}
                                    className={cn(
                                        'w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors flex gap-3',
                                        !n.read && 'bg-lab-blue/5',
                                    )}
                                >
                                    <div className="mt-0.5 shrink-0">
                                        <Flag className={cn('w-3.5 h-3.5', n.read ? 'text-lab-mute' : 'text-lab-blue')} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className={cn('text-xs', n.read ? 'text-lab-mute' : 'text-white')}>
                                            {n.title}
                                        </div>
                                        <div className="text-lab-mute text-[11px] mt-0.5 leading-snug">
                                            {n.body}
                                        </div>
                                        <div className="lab-mono text-[9px] text-lab-dim mt-1">
                                            {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                                        </div>
                                    </div>
                                    {!n.read && (
                                        <span className="ml-auto mt-1 w-1.5 h-1.5 rounded-full bg-lab-blue shrink-0" />
                                    )}
                                </button>
                            ))}
                        </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
